/**
 * @file `HypervisorPanel` — root component of the `/hypervisors` route
 *       (umbrella #883 part #891; control plane #1258 Part H / #1340).
 * @module @labcraft/admin/ui/hypervisor/hypervisor-panel
 *
 * Capacity strip + machine table (live `state` pills + a pinned-`content`
 * column) + a `LowerSplit` instance-detail pane, on the shared chrome. The
 * panel is the operator control plane: it reads each machine's live
 * lifecycle state (#1337) and engagement pin (#1340), and the lower-pane
 * detail carries the state-gated operator actions from umbrella §D's
 * state → allowed-admin-actions table — **Observe (live)** (a read-only pty
 * mirror in a right-edge drawer, {@link ObserveTerminal}) and **Suspend**
 * are afforded ONLY on a `running` VM (a live pty), and **Force-expire pin**
 * on any VM carrying a content pin. Suspend / force-expire sit behind a
 * confirm. Rows stay fresh off the hypervisor's fleet-lifecycle stream
 * ({@link useHypervisorFleetLifecycle}, with a reconcile-poll backstop).
 * When the daemon has no `LC_ADMIN_HYPERVISOR_URL` the panel renders an
 * honest "not connected yet" surface (empty table, `—` capacity). The
 * `learner` column is resolved admin-side from each machine id's learner id
 * against `@labcraft/account`; the `ttl` column still reads `—` until the
 * retention source is wired in. (The course/chapter column was dropped —
 * its clean source, the learner's current chapter, sits behind the #179
 * admin↔account wall.)
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type JSX,
} from 'react'
import { useClient } from '@labcraft/core/frontend/react'
import {
  useForceExpirePinMutation,
  useHypervisorFleetLifecycle,
  useHypervisorMachines,
  useOperatorSuspendMutation,
  usePagedList,
} from '../hooks/index.ts'
import type { Admin } from '../../client/index.ts'
import type { AdminMachine } from '../../shared/hypervisor_routes.ts'
import { LowerSplit } from '../kernel/lower_split.tsx'
import { Pager } from '../kernel/pager.tsx'
import { AdminTerminalView } from './terminal_view.tsx'
import { HypervisorView, type MachineFilter } from './view.ts'

/** Render the Hypervisors route. */
export function HypervisorPanel(): JSX.Element {
  const query = useHypervisorMachines()
  // Keep the panel fresh off the owner's fleet-lifecycle fan-out (#1340):
  // every Machine transition invalidates the list so rows + affordances
  // re-derive from reality without waiting on the reconcile poll.
  useHypervisorFleetLifecycle()
  const connected = query.data?.connected ?? false
  const machines = useMemo(() => query.data?.machines ?? [], [query.data])

  const [filter, setFilter] = useState<MachineFilter>('all')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [observeId, setObserveId] = useState<string | null>(null)

  const rows = useMemo(
    () => HypervisorView.filter(machines, filter),
    [machines, filter],
  )
  const capacity = HypervisorView.capacity(machines)
  const selected = machines.find(m => m.id === selectedId) ?? null

  const scrollRef = useRef<HTMLDivElement>(null)
  const { page, setPage, resetPage, pageSize, rowHeight, offset, pageCount } =
    usePagedList(scrollRef, rows.length)
  // A rail filter change starts a fresh page.
  useEffect(() => {
    resetPage()
  }, [filter, resetPage])
  const paged = rows.slice(offset, offset + pageSize)
  const observed = machines.find(m => m.id === observeId) ?? null
  const errorMessage = query.isError
    ? query.error.message || 'Load failed.'
    : null

  // If an observed machine leaves the list (shut down / reaped), close
  // the drawer rather than leaving it open over a stale id.
  useEffect(() => {
    if (observeId !== null && !machines.some(m => m.id === observeId)) {
      setObserveId(null)
    }
  }, [machines, observeId])

  const RAIL: ReadonlyArray<{
    id: MachineFilter
    label: string
    count: number
  }> = [
    { id: 'all', label: 'All', count: machines.length },
    { id: 'running', label: 'Running', count: capacity.running },
    {
      id: 'saved',
      label: 'Saved',
      count: machines.filter(m => m.state === 'saved').length,
    },
    {
      id: 'incidents',
      label: 'Incidents',
      count: machines.filter(HypervisorView.isIncident).length,
    },
  ]

  return (
    <div className="panel-split hv-root">
      <nav className="panel-rail" aria-label="Machine filters">
        <div className="panel-rail__h">Fleet</div>
        {RAIL.map(r => (
          <button
            key={r.id}
            className="panel-rail__item"
            aria-current={filter === r.id ? 'page' : undefined}
            onClick={() => setFilter(r.id)}>
            <span>{r.label}</span>
            <span className="panel-rail__count">{r.count}</span>
          </button>
        ))}
        <div className="panel-rail__foot">
          // select a machine → Observe (live) opens the read-only terminal
        </div>
      </nav>

      <div className="panel panel--no-subbar">
        <header className="panel-head">
          <div>
            <div className="panel-head__title-row">
              <h1 className="panel-head__title">Hypervisors</h1>
              <span className="panel-head__count">
                {connected
                  ? `${capacity.running} running / ${capacity.total}`
                  : '—'}
              </span>
            </div>
            <p className="panel-head__sub">
              // learner sandbox fleet (Firecracker microVMs)
            </p>
          </div>
        </header>

        {!connected && query.isSuccess && (
          <div className="panel-banner">
            <b>Not connected yet.</b> The admin daemon has no path to the
            hypervisor daemon. The table lights up once an admin→hypervisor
            connection is wired; lifecycle state, content pin, and the learner
            populate with it.
          </div>
        )}
        {errorMessage !== null && (
          <div className="panel-banner panel-banner--error">
            <b>Could not load machines.</b> {errorMessage}
          </div>
        )}

        <LowerSplit
          open={selected !== null}
          storageKey="lc-hypervisor-split"
          detail={
            selected && (
              <MachineDetail
                machine={selected}
                onClose={() => setSelectedId(null)}
                onObserve={setObserveId}
              />
            )
          }>
          <div className="paged-list">
            <div
              className="table-scroll"
              ref={scrollRef}
              style={
                rowHeight > 0
                  ? ({ '--row-h': `${rowHeight}px` } as CSSProperties)
                  : undefined
              }>
              <table className="table">
                <thead>
                  <tr>
                    <th>machine</th>
                    <th>learner</th>
                    <th>state</th>
                    <th>content</th>
                    <th>host</th>
                    <th>attached</th>
                    <th>ttl</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="table-empty">
                        {query.isPending
                          ? '// loading…'
                          : connected
                            ? '// no machines match this filter.'
                            : '// hypervisor not connected — no machines to show.'}
                      </td>
                    </tr>
                  ) : (
                    paged.map(m => (
                      <tr
                        key={m.id}
                        aria-selected={m.id === selectedId}
                        onClick={() => setSelectedId(m.id)}>
                        <td className="cell-code">{m.name}</td>
                        <td>
                          <LearnerCell learner={m.learner} />
                        </td>
                        <td>
                          {m.state ? (
                            <span
                              className={`spill spill--${HypervisorView.tone(m.state)}`}>
                              {m.state}
                            </span>
                          ) : (
                            <span className="cell-empty">—</span>
                          )}
                        </td>
                        <td>
                          <PinCell pin={m.pin} />
                        </td>
                        <td className="cell-meta">{m.hostIp}</td>
                        <td className="cell-meta">
                          {m.attached ? 'yes' : 'no'}
                        </td>
                        <td className="cell-empty">—</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="paged-foot">
              <Pager
                page={page}
                pageCount={pageCount}
                onPage={setPage}
                total={query.data ? rows.length : undefined}
              />
            </div>
          </div>
        </LowerSplit>
      </div>

      {/* Read-only live pty mirror — full-height right-edge drawer. The
          backdrop mounts only while open; the aside stays mounted so the
          slide transition animates, but its terminal (and the WS it
          opens) mounts only when a machine is being observed. */}
      {observed !== null && (
        <div
          className="observe-backdrop"
          onClick={() => setObserveId(null)}
          aria-hidden="true"
        />
      )}
      <aside
        className={`drawer observe-drawer ${observed !== null ? 'is-open' : ''}`}
        aria-label="Live terminal observer"
        aria-hidden={observed === null}>
        {observed !== null && (
          <ObserveTerminal
            machine={observed}
            onClose={() => setObserveId(null)}
          />
        )}
      </aside>
    </div>
  )
}

////////////////////////////////////////////////////////////////////////////////
// Instance detail (lower pane)
////////////////////////////////////////////////////////////////////////////////

/**
 * Compact "pinned content version" cell for the fleet table (umbrella
 * #1258 Part D / #1340). Renders the topmost pinned layer ref with a
 * `stale` badge when the pin will clean-refresh on its next resume; `—`
 * when the machine carries no pin.
 */
function PinCell(props: { pin: AdminMachine['pin'] }): JSX.Element {
  const version = HypervisorView.pinVersion(props.pin)
  if (version === null) return <span className="cell-empty">—</span>
  const stale = HypervisorView.pinStale(props.pin)
  return (
    <span className="hv-pin">
      <span className="cell-code">{version}</span>
      {stale && <span className="spill spill--warn hv-pin__stale">stale</span>}
    </span>
  )
}

/**
 * Fleet-table learner cell — the resolved learner's display name (email on
 * hover), or `—` when the machine carries no resolved learner (a
 * learner-less id or an account-lookup miss).
 */
function LearnerCell(props: { learner: AdminMachine['learner'] }): JSX.Element {
  const { learner } = props
  if (!learner) return <span className="cell-empty">—</span>
  return (
    <span className="cell-meta" title={learner.email}>
      {learner.displayName}
    </span>
  )
}

/** Machine detail rendered in the LowerSplit lower pane. */
function MachineDetail(props: {
  machine: AdminMachine
  onClose: () => void
  onObserve: (id: string) => void
}): JSX.Element {
  const { machine, onClose, onObserve } = props
  const suspend = useOperatorSuspendMutation()
  const forceExpire = useForceExpirePinMutation()
  const affordance = HypervisorView.affordance(machine.state)
  const pinVersion = HypervisorView.pinVersion(machine.pin)

  function onSuspend(): void {
    if (
      window.confirm(
        `Suspend ${machine.name}? A live learner (if any) sees "an operator ` +
          `paused your lab"; the VM snapshots to disk and can resume later.`,
      )
    ) {
      suspend.mutate(machine.id)
    }
  }

  function onForceExpire(): void {
    if (
      window.confirm(
        `Force-expire the content pin for ${machine.name}? The learner's next ` +
          `resume clean-refreshes onto current content instead of restoring ` +
          `their saved session. This cannot be undone.`,
      )
    ) {
      forceExpire.mutate(machine.id)
    }
  }

  return (
    <div>
      <div className="detail-head">
        <span className="detail-head__crumb">
          Hypervisors › <b>{machine.name}</b>
        </span>
        <button
          className="detail-head__close"
          onClick={onClose}
          aria-label="Close detail">
          ✕
        </button>
      </div>

      <section
        className="detail-section"
        style={{ marginTop: 0, borderTop: 0, paddingTop: 18 }}>
        <div className="hv-detail-head-row">
          <h3 className="detail-section__h">Machine</h3>
          <div className="hv-detail-actions">
            {affordance.observe && (
              <button
                className="lc-btn lc-btn--primary"
                onClick={() => onObserve(machine.id)}>
                Observe (live)
              </button>
            )}
            {affordance.suspend && (
              <button
                className="lc-btn lc-btn--danger"
                disabled={suspend.isPending}
                onClick={onSuspend}>
                {suspend.isPending ? 'Suspending…' : 'Suspend'}
              </button>
            )}
          </div>
        </div>
        <dl className="kv-grid">
          <dt>id</dt>
          <dd>{machine.id}</dd>
          <dt>kind</dt>
          <dd>{machine.kind}</dd>
          <dt>host ip</dt>
          <dd>{machine.hostIp}</dd>
          <dt>guest ip</dt>
          <dd>{machine.guestIp}</dd>
          <dt>tap</dt>
          <dd>{machine.tap}</dd>
          <dt>pty id</dt>
          <dd>{machine.ptyId}</dd>
          <dt>attached</dt>
          <dd>{machine.attached ? 'yes' : 'no'}</dd>
          <dt>state</dt>
          <dd className={machine.state ? '' : 'empty'}>
            {machine.state ?? '—'}
          </dd>
        </dl>
      </section>

      <section className="detail-section">
        <div className="hv-detail-head-row">
          <h3 className="detail-section__h">Pinned content</h3>
          {machine.pin && (
            <button
              className="lc-btn lc-btn--ghost"
              disabled={forceExpire.isPending}
              onClick={onForceExpire}>
              {forceExpire.isPending ? 'Force-expiring…' : 'Force-expire pin'}
            </button>
          )}
        </div>
        {machine.pin ? (
          <dl className="kv-grid">
            <dt>version</dt>
            <dd className="cell-code">{pinVersion}</dd>
            <dt>stack</dt>
            <dd className="cell-code">{machine.pin.layers.join(' → ')}</dd>
            <dt>fingerprint</dt>
            <dd
              className={machine.pin.stackFingerprint ? 'cell-code' : 'empty'}>
              {machine.pin.stackFingerprint ?? '—'}
            </dd>
            <dt>status</dt>
            <dd>
              {machine.pin.stale ? (
                <span className="spill spill--warn">
                  stale — resumes clean-refresh
                </span>
              ) : (
                <span className="spill spill--ok">live — resumes restore</span>
              )}
            </dd>
          </dl>
        ) : (
          <div className="empty-block">
            <b>No content pin.</b> This machine carries no engagement
            overlay-stack pin (a pin-less session or the KVM-free fake tier).
          </div>
        )}
      </section>

      <section className="detail-section">
        <h3 className="detail-section__h">Learner</h3>
        {machine.learner ? (
          <dl className="kv-grid">
            <dt>name</dt>
            <dd>{machine.learner.displayName}</dd>
            <dt>email</dt>
            <dd className="cell-code">{machine.learner.email}</dd>
            <dt>id</dt>
            <dd className="cell-code">{machine.learner.id}</dd>
          </dl>
        ) : (
          <div className="empty-block">
            <b>No learner resolved.</b> This machine's id carries no learner, or
            the account lookup returned nothing (e.g. a stale VM whose learner
            row was deleted).
          </div>
        )}
      </section>
    </div>
  )
}

////////////////////////////////////////////////////////////////////////////////
// Observe terminal (right-edge drawer)
////////////////////////////////////////////////////////////////////////////////

/**
 * Read-only live pty mirror rendered inside the observe drawer. LIVE
 * indicator + READ-ONLY badge + wire path in the footer; Esc closes.
 * The terminal mirrors the learner's dimensions ({@link AdminTerminalView}
 * resizes xterm to the pushed winsize), and the drawer is content-sized,
 * so it grows/shrinks with the learner's terminal.
 */
function ObserveTerminal(props: {
  machine: AdminMachine
  onClose: () => void
}): JSX.Element {
  const { machine, onClose } = props
  const client = useClient<Admin>()
  const [dims, setDims] = useState<{ cols: number; rows: number } | null>(null)

  // One observer driver per observed machine. The view's cleanup tears
  // the WS down when the drawer content unmounts (on close).
  const terminal = useMemo(
    () => client.hypervisor.terminal(machine.id),
    [client, machine.id],
  )
  const onDimensions = useCallback((cols: number, rows: number) => {
    setDims({ cols, rows })
  }, [])

  // Esc closes the drawer, matching the design.
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <>
      <div className="obs-head">
        <span className="obs-live">live</span>
        <span className="obs-title">
          {machine.id}
          <small>
            {machine.kind} · {machine.hostIp}
          </small>
        </span>
        <span className="obs-ro">read-only</span>
        <button
          className="obs-close"
          onClick={onClose}
          aria-label="Close observer">
          ✕
        </button>
      </div>
      <div className="obs-term">
        <AdminTerminalView terminal={terminal} onDimensions={onDimensions} />
      </div>
      <div className="obs-foot">
        <span>
          pty <span className="ok">attached</span>
        </span>
        <span>ws /api/hypervisor/{machine.id}/terminal</span>
        <span style={{ marginLeft: 'auto' }}>
          {dims ? `${dims.cols}×${dims.rows} · ` : ''}observing · input disabled
        </span>
      </div>
    </>
  )
}
